import type { Schemas } from '@npc/api-client'
import { copyText } from '@npc/platform'
import { useState } from 'react'

import { useCreateInvitation } from '../../app/queries'
import { ApiFailure } from '../../app/errors'
import { Modal } from '../../components/modal'
import './team.css'
import { StepUpDialog } from '../../components/step-up'
import { t } from '../../copy'
import { T } from '../../copy/text'
import { ProjectPicker } from './project-picker'

/**
 * 「叫个人进来」那张表：邮箱、身份、能碰哪些项目 → 当场再验一次 → 发邀请。
 *
 * 加人是改团队权限（CLAUDE.md §10 点名的四件之一），所以中间夹一道 StepUpDialog；
 * 二次验证的资源串要与服务端 `auth.TeamInvitationResource` 一个字不差：
 * `team-invitation?email=<规范化邮箱>&role=<小写角色>`（键按字母序，Go 的 url.Values.Encode）。
 *
 * 发出去之后给链接：信发出去了就说「收到邮件点一下就能进来」；没发出去（没配发信通道）
 * 就明说「把这个链接发给他」。链接不带秘密——他用那个邮箱登录，身份按邮箱配。
 */
export function InviteDialog({
  open,
  roles,
  projects,
  onClose,
}: {
  open: boolean
  roles: readonly Schemas['MemberRoleInfo'][]
  projects: readonly Schemas['Project'][]
  onClose: () => void
}) {
  const create = useCreateInvitation()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Schemas['MemberRole']>('operator')
  const [picked, setPicked] = useState<readonly string[]>([])
  const [problem, setProblem] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [issued, setIssued] = useState<Schemas['InvitationIssued'] | null>(null)
  const [copied, setCopied] = useState(false)

  const normalizedEmail = email.trim().toLowerCase()
  const needsProjects = role !== 'owner'
  const roleSummary = roles.find((item) => item.role === role)?.summary

  function reset() {
    setEmail('')
    setRole('operator')
    setPicked([])
    setProblem(null)
    setVerifying(false)
    setIssued(null)
    setCopied(false)
  }

  function submit() {
    setProblem(null)
    if (normalizedEmail === '' || !normalizedEmail.includes('@')) {
      setProblem(t('team.invite.form.email'))
      return
    }
    if (needsProjects && picked.length === 0) {
      setProblem(t('team.invite.needs_projects'))
      return
    }
    setVerifying(true)
  }

  async function issue(stepUpToken: string) {
    setVerifying(false)
    try {
      const out = await create.mutateAsync({
        email: normalizedEmail,
        role,
        projects: needsProjects ? picked : [],
        stepUpToken,
      })
      setIssued(out)
    } catch (cause) {
      // 验证被拒不当死句丢回表单，重开验证框再走一遍（见 member-dialog 同一处）。
      if (cause instanceof ApiFailure && cause.kind === 'needs_passkey') {
        setVerifying(true)
        return
      }
      setProblem(cause instanceof ApiFailure ? cause.message : t('error.generic'))
    }
  }

  const resource = `team-invitation?email=${encodeURIComponent(normalizedEmail)}&role=${encodeURIComponent(role)}`
  const days = issued ? Math.max(1, Math.round((Date.parse(issued.expires_at ?? '') - Date.now()) / 86_400_000)) : 0

  return (
    <>
      <Modal
        className="team-theme"
        open={open && !verifying}
        onOpenChange={(next) => {
          if (!next) {
            reset()
            onClose()
          }
        }}
        title={t('team.invite.form.title')}
      >
        {issued ? (
          <div data-testid="team-invite-issued" className="flex flex-col gap-npc-gap">
            <p className="m-0 text-npc-body">
              {t('team.invite.sent', { email: issued.email, days: String(days) })}
            </p>
            {issued.emailed ? null : (
              <p className="m-0 text-npc-body-sm text-npc-ink-soft" data-testid="team-invite-manual">
                <T id="team.invite.link_manual" />
              </p>
            )}
            <div className="text-npc-caption text-npc-ink-soft">
              <T id="team.invite.link" />
            </div>
            <code data-testid="team-invite-link" className="break-all rounded-npc-code bg-npc-surface px-[8px] py-[6px] text-npc-caption">
              {issued.link}
            </code>
            <div className="flex gap-npc-gap-sm">
              <button
                type="button"
                data-testid="team-invite-copy"
                onClick={() => {
                  // 走 packages/platform，不直接摸 navigator.clipboard
                  // （CLAUDE.md §4.2：前端不碰任何本机 API）。
                  //
                  // **而且「复制好了」只在真的复制成功之后才说。**
                  // 原来是无条件 setCopied(true)：Safari 里不是用户手势
                  // 触发、或者页面没聚焦时 writeText 会被拒，而按钮照样
                  // 变成「复制好了」——他去粘贴，粘出来的是上一次的东西，
                  // 而这条链接**只显示这一次**。
                  void copyText(issued.link ?? '').then((r) => {
                    if (r.ok) setCopied(true)
                  })
                }}
                className="rounded-npc-button border border-npc-border-soft px-[10px] py-[4px] text-npc-caption"
              >
                <T id={copied ? 'team.invite.link_copied' : 'team.invite.link_copy'} />
              </button>
              <button
                type="button"
                data-testid="team-invite-done"
                onClick={() => {
                  reset()
                  onClose()
                }}
                className="rounded-npc-button bg-npc-green px-[10px] py-[4px] text-npc-caption text-white"
              >
                <T id="team.invite.done" />
              </button>
            </div>
          </div>
        ) : (
          <form
            data-testid="team-invite-form"
            className="flex flex-col gap-npc-gap"
            onSubmit={(event) => {
              event.preventDefault()
              submit()
            }}
          >
            <label className="flex flex-col gap-[4px] text-npc-caption text-npc-ink-soft">
              <T id="team.invite.form.email" />
              <input
                data-testid="team-invite-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="rounded-npc-button border border-npc-border-soft px-[10px] py-[6px] text-npc-body text-npc-ink"
              />
            </label>
            <label className="flex flex-col gap-[4px] text-npc-caption text-npc-ink-soft">
              <T id="team.invite.form.role" />
              <select
                data-testid="team-invite-role"
                value={role}
                onChange={(event) => setRole(event.target.value as Schemas['MemberRole'])}
                className="rounded-npc-button border border-npc-border-soft px-[10px] py-[6px] text-npc-body text-npc-ink"
              >
                {roles.map((item) => (
                  <option key={item.role} value={item.role}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            {/* 与改身份表同一句：这一档到底是什么，就写在选完的下面。
                文字是服务端渲染好的 roles[].summary，前端不另写一版。 */}
            {roleSummary ? (
              <p className="m-0 -mt-[6px] text-npc-meta text-npc-ink-softer" data-testid="team-invite-role-summary">
                {roleSummary}
              </p>
            ) : null}
            <ProjectPicker
              projects={projects}
              picked={picked}
              onChange={setPicked}
              ownerSeesAll={!needsProjects}
              testIdPrefix="team-invite"
              legendId="team.invite.form.projects"
              hintId="team.invite.form.projects_hint"
            />
            {problem ? (
              <p className="m-0 text-npc-body-sm text-npc-red-ink" data-testid="team-invite-problem">
                {problem}
              </p>
            ) : null}
            <div className="flex justify-end gap-npc-gap-sm">
              <button
                type="button"
                data-testid="team-invite-cancel"
                onClick={() => {
                  reset()
                  onClose()
                }}
                className="rounded-npc-button border border-npc-border-soft px-[10px] py-[4px] text-npc-caption"
              >
                <T id="team.invite.form.cancel" />
              </button>
              <button
                type="submit"
                data-testid="team-invite-submit"
                disabled={create.isPending}
                className="rounded-npc-button bg-npc-green px-[10px] py-[4px] text-npc-caption text-white disabled:opacity-50"
              >
                <T id="team.invite.form.submit" />
              </button>
            </div>
          </form>
        )}
      </Modal>
      {verifying ? (
        <StepUpDialog
          open
          intent="change_team_permission"
          resource={resource}
          title={t('team.invite.form.title')}
          description={t('team.invite.stepup')}
          onCancel={() => setVerifying(false)}
          onVerified={(token) => void issue(token)}
        />
      ) : null}
    </>
  )
}
